import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, MessageCircle, FileText, HelpCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import SupportChat from '../components/SupportChat';
import Footer from '../components/Footer';

const Support = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <ArrowLeft size={20} className="text-gray-500" />
              <span className="font-bold text-xl text-gray-900">ListingPro AI</span>
            </Link>
            <Link to="/login">
              <Button variant="outline">Sign In</Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">How can we help?</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Chat with our AI assistant Luna, browse our help resources, or contact our support team.
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Chat Section - Takes 2 columns */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <MessageCircle className="text-blue-600" size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Chat with Luna</h2>
                  <p className="text-gray-500 text-sm">Our AI assistant is available 24/7</p>
                </div>
              </div>
              <SupportChat isPage={true} />
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Contact Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Mail size={18} className="text-gray-500" />
                Contact Support
              </h3>
              <p className="text-gray-600 text-sm mb-4">
                Need help with your account, billing, or have a complex issue? Our team is here to help.
              </p>
              <a 
                href="mailto:erick@listingpro-ai.com"
                className="block w-full text-center bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium py-2.5 px-4 rounded-lg transition-colors"
              >
                erick@listingpro-ai.com
              </a>
              <p className="text-xs text-gray-500 mt-3 text-center">
                Usually responds within 24 hours
              </p>
            </div>

            {/* Quick Links */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <HelpCircle size={18} className="text-gray-500" />
                Quick Links
              </h3>
              <div className="space-y-2">
                <Link to="/pricing" className="block text-blue-600 hover:text-blue-700 text-sm py-1">
                  → Pricing & Plans
                </Link>
                <Link to="/faq" className="block text-blue-600 hover:text-blue-700 text-sm py-1">
                  → Frequently Asked Questions
                </Link>
                <Link to="/login" className="block text-blue-600 hover:text-blue-700 text-sm py-1">
                  → Sign In to Your Account
                </Link>
                <Link to="/signup" className="block text-blue-600 hover:text-blue-700 text-sm py-1">
                  → Create an Account
                </Link>
              </div>
            </div>

            {/* Common Topics */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border border-blue-100 p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <FileText size={18} className="text-blue-600" />
                Popular Topics
              </h3>
              <ul className="space-y-3 text-sm text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-blue-500 mt-0.5">•</span>
                  <span>How to create your first listing</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-500 mt-0.5">•</span>
                  <span>Using AI virtual staging</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-500 mt-0.5">•</span>
                  <span>Generating market updates</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-500 mt-0.5">•</span>
                  <span>Managing your subscription</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-500 mt-0.5">•</span>
                  <span>Setting up your agent profile</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Support;
